/**
 * Phase 5Z.36 · Phase 3 — HR Employee Widget
 * --------------------------------------------
 * Compact at-a-glance HR card for one employee. Used on:
 *   • Mitarbeiter-Profil (Header above tabs)
 *   • Admin Dashboard (e.g. "Mein Team" overview)
 *   • Wherever a manager needs a quick employee HR snapshot
 *
 * Data source: GET /api/hr/widget/{employee_id}
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plane, HeartPulse, Clock4, AlertTriangle, FileWarning, Loader2, Pencil } from "lucide-react";

export default function HrEmployeeWidget({ employeeId, compact = false, className = "", onEditCarryOver }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!employeeId) return;
    let cancelled = false;
    setData(null);
    setError(null);
    api.get(`/hr/widget/${employeeId}`).then(r => {
      if (!cancelled) setData(r.data);
    }).catch(e => {
      if (!cancelled) setError(e.response?.data?.detail || "HR-Daten nicht verfügbar");
    });
    return () => { cancelled = true; };
  }, [employeeId]);

  if (!employeeId) return null;
  if (error) {
    return (
      <div className={`bg-zinc-900 border border-zinc-800 rounded-lg p-4 text-xs text-zinc-500 ${className}`}
           data-testid="hr-widget-error">{error}</div>
    );
  }
  if (!data) {
    return (
      <div className={`bg-zinc-900 border border-zinc-800 rounded-lg p-4 text-xs text-zinc-500 flex items-center gap-2 ${className}`}
           data-testid="hr-widget-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Lade HR-Daten…
      </div>
    );
  }

  const v = data.vacation;
  const ot = data.overtime;
  const otNeg = ot.balance_minutes < 0;

  return (
    <div className={`bg-zinc-900 border border-zinc-800 rounded-lg p-4 ${className}`}
         data-testid="hr-employee-widget">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
          HR · Übersicht
        </div>
        <div className="flex items-center gap-2">
          {onEditCarryOver && (
            <button onClick={onEditCarryOver} data-testid="hr-widget-edit-carry-link"
              className="text-[11px] text-indigo-300 hover:text-indigo-200 inline-flex items-center gap-1 font-semibold">
              Resturlaub bearbeiten <Pencil className="w-3 h-3" />
            </button>
          )}
          {data.sick.currently_on_sick_leave && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-200 border border-rose-500/40 inline-flex items-center gap-1">
              <HeartPulse className="w-3 h-3" /> Im Krankenstand
            </span>
          )}
        </div>
      </div>

      {/* KPI tiles */}
      <div className={`grid ${compact ? "grid-cols-3" : "grid-cols-2 md:grid-cols-4"} gap-2`}>
        <WidgetTile icon={Plane} label="Resturlaub"
          value={v.remaining.toFixed(1)} suffix="T"
          accent="text-emerald-300"
          testId="hr-widget-vac-remaining"
          subline={v.carry_over_total > 0 ? `+${v.carry_over_total.toFixed(1)} Vorjahr` : null}
          warn={v.carry_over_expiring_90d_days > 0
                ? `${v.carry_over_expiring_90d_days.toFixed(1)} laufen ab` : null} />
        <WidgetTile icon={Clock4} label="Überstunden"
          value={ot.balance_hours.toFixed(1)} suffix="h"
          accent={otNeg ? "text-rose-300" : "text-blue-300"}
          testId="hr-widget-ot" />
        <WidgetTile icon={HeartPulse} label="Krank YTD"
          value={data.sick.days_this_year} suffix="d"
          accent="text-rose-300"
          testId="hr-widget-sick-ytd" />
        {!compact && (
          <WidgetTile icon={AlertTriangle} label="Offen"
            value={data.pending_requests} suffix=""
            accent="text-amber-300"
            testId="hr-widget-pending"
            subline={data.pending_requests > 0 ? "Anträge" : null} />
        )}
      </div>

      {/* Footer warnings */}
      {(data.expiring_documents > 0 || data.missing_time_entries_30d > 2) && (
        <div className="mt-3 pt-3 border-t border-zinc-800 space-y-1.5">
          {data.expiring_documents > 0 && (
            <div className="text-[11px] text-amber-300 flex items-center gap-1.5"
                 data-testid="hr-widget-docs-warning">
              <FileWarning className="w-3.5 h-3.5" />
              {data.expiring_documents} Dokument{data.expiring_documents !== 1 ? "e" : ""} läuft in 60 Tagen ab
            </div>
          )}
          {data.missing_time_entries_30d > 2 && (
            <div className="text-[11px] text-amber-300 flex items-center gap-1.5"
                 data-testid="hr-widget-missing-time">
              <AlertTriangle className="w-3.5 h-3.5" />
              {data.missing_time_entries_30d} Werktage ohne Zeiterfassung (30 Tg)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WidgetTile({ icon: Icon, label, value, suffix, accent, testId, subline, warn }) {
  return (
    <div className="bg-zinc-950/40 border border-zinc-800 rounded p-2" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
        <Icon className="w-3 h-3" />
        <span className="truncate">{label}</span>
      </div>
      <div className={`text-2xl font-mono font-bold tabular-nums mt-1 ${accent}`}>
        {value}<span className="text-xs text-zinc-500 ml-1">{suffix}</span>
      </div>
      {subline && <div className="text-[10px] text-zinc-500 truncate">{subline}</div>}
      {warn && (
        <div className="text-[10px] text-amber-300 mt-0.5 flex items-center gap-1">
          <AlertTriangle className="w-2.5 h-2.5" /> {warn}
        </div>
      )}
    </div>
  );
}
